import {
  Button,
  Column,
  FlexBox,
  IconButton,
  Table,
  Text,
} from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { ALL_PLATFORMS } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useState } from 'react';

import { ajaxSend } from 'api/api';
import { ConfirmModal } from 'components/shared/confirmModal/components/confirmModal';
import { useNotifications } from 'hooks/useNotifications';
import { usePlatformApplications } from 'hooks/usePlatformApplications';

import { ListPlatformsBody } from './components/body/listPlatformsBody';
import { SubmitModal } from './components/submitModal/submitModal';
import { mapPlatform } from './platforms';

type ModalType = 'submit' | 'delete';

type ListPlatformsImplProps = {
  messengerId: string;
};

export const ListPlatforms = ({
  messengerId,
}: ListPlatformsImplProps): JSX.Element => {
  const notify = useNotifications();
  const { asyncStatus, items, refreshItems } =
    usePlatformApplications(messengerId);
  const [activeModalName, setActiveModalName] = useState<null | ModalType>(
    null,
  );
  const [selectedPlatformId, setSelectedPlatformId] = useState('');

  const hasChannelSetupAccess = useHasAccess(
    'settings.integration.channel.setup',
  );

  const showSpecificModal = (modal: ModalType, platformId?: string) => {
    ampli.pushNotificationPlatformFormOpened({ mode: modal });
    setActiveModalName(modal);
    if (platformId) {
      setSelectedPlatformId(platformId);
    }
  };
  const showAddModal = useCallback(() => {
    showSpecificModal('submit');
  }, []);
  const showUpdateModal = useCallback(
    (platformId: string) => showSpecificModal('submit', platformId),
    [],
  );
  const showDeleteModal = useCallback(
    (platformId: string) => showSpecificModal('delete', platformId),
    [],
  );

  const closeModal = useCallback(() => {
    if (activeModalName !== null) {
      ampli.pushNotificationPlatformFormCancelled({
        mode: activeModalName,
      });
    }
    setActiveModalName(null);
    setSelectedPlatformId('');
  }, [activeModalName]);

  const activePlatform = selectedPlatformId
    ? items.find((x) => x.id === selectedPlatformId)
    : null;

  const hasAvailablePlatforms =
    asyncStatus !== 'succeeded' || ALL_PLATFORMS.length > items.length;

  const deletePlatformModalCallback = async () => {
    if (!activePlatform) {
      return;
    }

    try {
      ampli.messengerFormSubmitted({ mode: 'delete' });
      await ajaxSend({
        path: `/v1/messengers/${messengerId}/platform_application/${activePlatform.platform.toLowerCase()}`,
        method: 'DELETE',
      });

      notify({
        title: 'Push notification platform deleted',
        text: `Push notification platform was deleted`,
        icon: 'done',
        className: 'success',
        time: 5000,
      });

      closeModal();
      refreshItems();
    } catch {
      notify({
        title: 'An error occurred',
        text: `An error occurred when trying to delete the push notification platform.`,
        icon: 'exclamation',
        className: 'danger',
      });
    }
  };

  return (
    <Column>
      <Column.Row slottable noHorizontalPadding noVerticalPadding noMinHeight>
        <Column.Slot placement="start">
          <Text.Heading variant="heading-3">All platforms</Text.Heading>
        </Column.Slot>

        <Column.Slot placement="end">
          <FlexBox alignItems="center" gap="200">
            <IconButton
              icon="sync"
              variant="secondary"
              disabled={asyncStatus === 'loading'}
              onClick={refreshItems}
            />

            {hasChannelSetupAccess && (
              <Button
                variant="secondary"
                onClick={showAddModal}
                disabled={
                  !messengerId ||
                  asyncStatus !== 'succeeded' ||
                  !hasAvailablePlatforms
                }
                id="add-platform-application-btn"
              >
                Add platform
              </Button>
            )}
          </FlexBox>
        </Column.Slot>
      </Column.Row>

      <Column.Row noHorizontalPadding noVerticalPadding>
        <Table framed stickyHeader headerWithBackground>
          <Table.Thead>
            <Table.Tr>
              <Table.Th columnId={null}>Platform</Table.Th>
              <Table.Th columnId={null}>Status</Table.Th>
              <Table.Th columnId={null}>Updated</Table.Th>
              <Table.Th columnId={null} />
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {messengerId ? (
              <ListPlatformsBody
                asyncStatus={asyncStatus}
                items={items}
                refreshItems={refreshItems}
                showUpdateModal={showUpdateModal}
                showDeleteModal={showDeleteModal}
              />
            ) : (
              <FlexBox justifyContent="center">
                <Text.Paragraph variant="1">
                  Messenger ID is unknown. Unable to get push notification
                  platforms
                </Text.Paragraph>
              </FlexBox>
            )}
          </Table.Tbody>
        </Table>
      </Column.Row>

      {/** MODALS */}
      <SubmitModal
        show={activeModalName === 'submit'}
        messengerId={messengerId as string}
        closeModal={closeModal}
        refreshItems={refreshItems}
        unavailablePlatforms={items.map((x) => x.platform)}
        editPlatform={activePlatform?.platform}
      />
      <ConfirmModal
        cancel={closeModal}
        cancelText="Cancel"
        confirm={deletePlatformModalCallback}
        confirmText="Yes, delete it now"
        headline="Are you sure?"
        isShown={activeModalName === 'delete' && activePlatform !== null}
        text={`Are you sure you want to delete "${mapPlatform(
          activePlatform?.platform,
        )}" platform?`}
      />
      <ConfirmModal
        cancel={closeModal}
        cancelText="Cancel"
        confirm={deletePlatformModalCallback}
        confirmText="Yes, delete it now"
        headline="Are you sure?"
        isShown={activeModalName === 'delete' && activePlatform !== null}
        text={`Are you sure you want to delete "${activePlatform?.platform}" push notification platform?`}
      />
    </Column>
  );
};
